import { handleActions } from 'redux-actions';
import {
  batchError,
  batchFinish,
  batchLoadGalleries,
  batchLoadGalleriesFailure,
  batchLoadGalleriesSuccess,
  batchLoadTiers,
  batchLoadTiersFailure,
  batchLoadTiersSuccess,
  batchStart,
  batchMerge,
  batchMergeSuccess,
  batchMergeFailure,
  cancelBatchAction,
  refreshTableSuccess,
  selectBatchAction,
  setNewModalLoading,
  setTableLayout,
  updateSelection,
} from '../actions/studio';
import {
  DiscountsSubsection,
  PlaygroundSubsection,
  StudioSection,
  StudioState,
  TableLayout,
  VideoModel,
  VideoCreatorErrorCode,
} from '../types/store';
import {
  initializePlayground,
  pullDrawingsTabStatusFailure,
  pullDrawingsTabStatusSuccess,
} from '../actions/studio/playground';
import { fetchBreadcrumbs, setBreadcrumbs } from '../actions/studio/page';
import { setIsBusy, updateSelectedFolder } from '../actions/studio/folders';
import { initializeDiscounts } from '../actions/studio/discounts';
import {
  initializeEarnings,
  updatePaypalState,
  updateBitpayState,
  updateStripeState,
} from '../actions/studio/earnings';
import {
  EMPTY_SELECTION_STATE,
  STUDIO_DEVIATIONS_STREAM,
} from '../../constants';
import {
  loadSettings,
  loadSettingsSuccess,
  loadSettingsFailure,
  loadGalleries,
  loadGalleriesSuccess,
  loadGalleriesFailure,
  saveSettings,
  saveSettingsSuccess,
  saveSettingsFailure,
  resetState,
} from '../actions/studio/gallerySettings';
import {
  updateVideoCreatorPrompt,
  updateVideoCreatorModel,
  updateVideoCreatorAspectRatio,
  updateVideoCreatorDuration,
  updateVideoCreatorSound,
  updateVideoCreatorStatus,
  updateVideoCreatorReferenceDeviation,
  updateVideoCreatorErrorCode,
  resetVideoCreator,
  initializeVideoCreator,
  updateVideoCreatorRestrictions,
  updateVideoCreatorPricePoints,
  updateVideoCreatorDidCreate,
  updateVideoCreatorIsCalculatingCost,
  updateVideoCreatorPendingGenerationId,
} from '../actions/studio/videoCreator';

// Import all handlers from separated files
import {
  // Batch handlers
  handleBatchStart,
  handleBatchError,
  handleBatchFinish,
  handleBatchLoadGalleries,
  handleBatchLoadGalleriesFailure,
  handleBatchLoadGalleriesSuccess,
  handleBatchLoadTiers,
  handleBatchLoadTiersFailure,
  handleBatchLoadTiersSuccess,
  handleBatchMerge,
  handleBatchMergeSuccess,
  handleBatchMergeFailure,
  // Discounts handlers
  handleInitializeDiscounts,
  // Earnings handlers
  handleInitializeEarnings,
  handleUpdatePaypalState,
  handleUpdateBitpayState,
  handleUpdateStripeState,
  // Folders handlers
  handleSetIsBusy,
  handleUpdateSelectedFolder,
  // Gallery Settings handlers
  handleLoadSettings,
  handleLoadSettingsSuccess,
  handleLoadSettingsFailure,
  handleLoadGalleries,
  handleLoadGalleriesSuccess,
  handleLoadGalleriesFailure,
  handleSaveSettings,
  handleSaveSettingsSuccess,
  handleSaveSettingsFailure,
  handleResetState,
  // Page handlers
  handleFetchBreadcrumbs,
  handleSetBreadcrumbs,
  // Playground handlers
  handleInitializePlayground,
  handlePullDrawingsTabStatusSuccess,
  handlePullDrawingsTabStatusFailure,
  // Table handlers
  handleRefreshTableSuccess,
  handleUpdateSelection,
  handleSelectBatchAction,
  handleCancelBatchAction,
  handleSetNewModalLoading,
  handleSetTableLayout,
  // Video Creator handlers
  handleUpdateVideoCreatorPrompt,
  handleUpdateVideoCreatorModel,
  handleUpdateVideoCreatorAspectRatio,
  handleUpdateVideoCreatorDuration,
  handleUpdateVideoCreatorSound,
  handleUpdateVideoCreatorStatus,
  handleUpdateVideoCreatorReferenceDeviation,
  handleUpdateVideoCreatorErrorCode,
  handleResetVideoCreator,
  handleInitializeVideoCreator,
  handleUpdateVideoCreatorRestrictions,
  handleUpdateVideoCreatorPricePoints,
  handleUpdateVideoCreatorDidCreate,
  handleUpdateVideoCreatorIsCalculatingCost,
  handleUpdateVideoCreatorPendingGenerationId,
} from './studio/index';

const defaultState: StudioState = {
  params: { section: StudioSection.DRAFTS },
  table: {
    streamId: STUDIO_DEVIATIONS_STREAM.streamId,
    pageSize: 0,
    layout: TableLayout.LIST,
  },
  freeCapacity: 0,
  maxCapacity: 0,
  resultsTotalCount: 0,
  duperbrowseStreamVersion: 0,
  galleries: undefined,
  tags: {},
  originalsData: {},
  tiers: undefined,
  selection: EMPTY_SELECTION_STATE,
  runningBatches: [],
  batchActionLoading: false,
  playground: {
    subsection: PlaygroundSubsection.NotMounted,
    isDrawingsTabVisible: false,
  },
  discounts: {
    subsection: DiscountsSubsection.Active,
    limitations: {
      numCampaignsAllowed: 0,
      numCampaignsRemaining: 0,
    },
  },
  folders: {
    status: 'idle',
  },
  videoCreator: {
    prompt: '',
    model: VideoModel.HAILUO,
    aspectRatio: '4:3',
    duration: 6,
    sound: false,
    status: 'idle',
    errorCode: VideoCreatorErrorCode.NO_ERROR,
    restrictions: undefined,
    pointsPackages: [],
    pricePoints: 0,
    didCreate: false,
    isCalculatingCost: false,
    pendingGenerationId: undefined,
  },
};

const reducer = handleActions<StudioState, any>(
  {
    // Table handlers
    [refreshTableSuccess.toString()]: handleRefreshTableSuccess,
    [updateSelection.toString()]: handleUpdateSelection,
    [selectBatchAction.toString()]: handleSelectBatchAction,
    [cancelBatchAction.toString()]: handleCancelBatchAction,
    [setNewModalLoading.toString()]: handleSetNewModalLoading,
    [setTableLayout.toString()]: handleSetTableLayout,

    // Batch handlers
    [batchStart.toString()]: handleBatchStart,
    [batchError.toString()]: handleBatchError,
    [batchFinish.toString()]: handleBatchFinish,
    [batchLoadGalleries.toString()]: handleBatchLoadGalleries,
    [batchLoadGalleriesFailure.toString()]: handleBatchLoadGalleriesFailure,
    [batchLoadGalleriesSuccess.toString()]: handleBatchLoadGalleriesSuccess,
    [batchLoadTiers.toString()]: handleBatchLoadTiers,
    [batchLoadTiersFailure.toString()]: handleBatchLoadTiersFailure,
    [batchLoadTiersSuccess.toString()]: handleBatchLoadTiersSuccess,
    [batchMerge.toString()]: handleBatchMerge,
    [batchMergeSuccess.toString()]: handleBatchMergeSuccess,
    [batchMergeFailure.toString()]: handleBatchMergeFailure,

    // Playground handlers
    [initializePlayground.toString()]: handleInitializePlayground,
    [pullDrawingsTabStatusSuccess.toString()]:
      handlePullDrawingsTabStatusSuccess,
    [pullDrawingsTabStatusFailure.toString()]:
      handlePullDrawingsTabStatusFailure,

    // Discounts handlers
    [initializeDiscounts.toString()]: handleInitializeDiscounts,

    // Earnings handlers
    [initializeEarnings.toString()]: handleInitializeEarnings,
    [updatePaypalState.toString()]: handleUpdatePaypalState,
    [updateBitpayState.toString()]: handleUpdateBitpayState,
    [updateStripeState.toString()]: handleUpdateStripeState,

    // Folders handlers
    [setIsBusy.toString()]: handleSetIsBusy,
    [updateSelectedFolder.toString()]: handleUpdateSelectedFolder,

    // Page/Breadcrumbs handlers
    [fetchBreadcrumbs.toString()]: handleFetchBreadcrumbs,
    [setBreadcrumbs.toString()]: handleSetBreadcrumbs,

    // Gallery Settings handlers
    [loadSettings.toString()]: handleLoadSettings,
    [loadSettingsSuccess.toString()]: handleLoadSettingsSuccess,
    [loadSettingsFailure.toString()]: handleLoadSettingsFailure,
    [loadGalleries.toString()]: handleLoadGalleries,
    [loadGalleriesSuccess.toString()]: handleLoadGalleriesSuccess,
    [loadGalleriesFailure.toString()]: handleLoadGalleriesFailure,
    [saveSettings.toString()]: handleSaveSettings,
    [saveSettingsSuccess.toString()]: handleSaveSettingsSuccess,
    [saveSettingsFailure.toString()]: handleSaveSettingsFailure,
    [resetState.toString()]: handleResetState,

    // Video Creator handlers
    [updateVideoCreatorPrompt.toString()]: handleUpdateVideoCreatorPrompt,
    [updateVideoCreatorModel.toString()]: handleUpdateVideoCreatorModel,
    [updateVideoCreatorAspectRatio.toString()]:
      handleUpdateVideoCreatorAspectRatio,
    [updateVideoCreatorDuration.toString()]: handleUpdateVideoCreatorDuration,
    [updateVideoCreatorSound.toString()]: handleUpdateVideoCreatorSound,
    [updateVideoCreatorStatus.toString()]: handleUpdateVideoCreatorStatus,
    [updateVideoCreatorReferenceDeviation.toString()]:
      handleUpdateVideoCreatorReferenceDeviation,
    [updateVideoCreatorErrorCode.toString()]: handleUpdateVideoCreatorErrorCode,
    [resetVideoCreator.toString()]: handleResetVideoCreator,
    [initializeVideoCreator.toString()]: handleInitializeVideoCreator,
    [updateVideoCreatorRestrictions.toString()]:
      handleUpdateVideoCreatorRestrictions,
    [updateVideoCreatorPricePoints.toString()]:
      handleUpdateVideoCreatorPricePoints,
    [updateVideoCreatorDidCreate.toString()]: handleUpdateVideoCreatorDidCreate,
    [updateVideoCreatorIsCalculatingCost.toString()]:
      handleUpdateVideoCreatorIsCalculatingCost,
    [updateVideoCreatorPendingGenerationId.toString()]:
      handleUpdateVideoCreatorPendingGenerationId,
  },
  defaultState
);

export default reducer;
